import { Dropdown, Typography, useTheme } from '@mui/joy';
import { useMemo, useState } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';

import { useAuthUser } from 'auth/hooks';
import { IMenuItem } from 'core/types';
import { hasPermission } from 'router/utils';
import { Color, Level, ModeType, Size, Variant } from 'theme/enums';

import { Label, MenuButtonWrapper, SubMenuItemWrapper, SubMenuWrapper, Wrapper } from './style';

interface IProps {
  text: string | JSX.Element;
  icon?: JSX.Element;
  endDecorator?: JSX.Element;
  subItems?: IMenuItem[];
  url?: string;
  onClick?: () => void;
  matchedPath?: string | string[];
}

const MenuItem = ({ text, icon, endDecorator, subItems, url, onClick, matchedPath }: IProps) => {
  const [subMenuOpen, setSubMenuOpen] = useState(false);

  const theme = useTheme();
  const { pathname } = useLocation();

  const { user } = useAuthUser();

  const resources = useMemo(() => user?.resources, [user]);

  const isDarkMode = theme.palette.mode === ModeType.DARK;

  const subMenuLinkStyle = {
    fontSize: 14,
    padding: '12px 16px',
    color: isDarkMode ? theme.palette.common.black : theme.palette.common.white,
  };

  const checkIfPathMatches = (
    itemMatchedPath?: string | string[],
    itemUrl?: string,
    currentPathname?: string
  ) => {
    const pathsToMatch = Array.isArray(itemMatchedPath)
      ? itemMatchedPath
      : [itemMatchedPath || (itemUrl ? `${itemUrl}/*` : '')];

    return pathsToMatch.some((path) => path && matchPath(path, currentPathname));
  };

  const isSelected = checkIfPathMatches(matchedPath, url, pathname);

  if (!subItems) {
    return (
      <Wrapper to={url || '#'} onClick={onClick && onClick} active={isSelected}>
        {icon && icon}
        <Label level={Level.TITLE_SM} color={Color.NEUTRAL}>
          {text}
        </Label>
      </Wrapper>
    );
  }

  return (
    <Dropdown onOpenChange={(_, open) => setSubMenuOpen(open)}>
      <MenuButtonWrapper active={subMenuOpen || isSelected} endDecorator={endDecorator}>
        {icon && icon}
        <Label level={Level.TITLE_SM} color={Color.NEUTRAL}>
          {text}
        </Label>
      </MenuButtonWrapper>
      <SubMenuWrapper placement="right-start" size={Size.SM}>
        {subItems.map((subItem) => {
          const hasSubItemPermission = hasPermission(subItem?.permissions, resources);

          const isSubItemSelected = checkIfPathMatches(
            subItem?.matchedPath,
            subItem?.url,
            pathname
          );

          if (!hasSubItemPermission || subItem?.hide) {
            return null;
          }

          return (
            <SubMenuItemWrapper
              key={subItem?.name}
              selected={isSubItemSelected}
              color={Color.NEUTRAL}
              variant={Variant.SOLID}
              onClick={() => {
                subItem?.onClick?.();

                setSubMenuOpen(false);
              }}
            >
              {subItem?.url ? (
                <NavLink to={subItem.url} style={subMenuLinkStyle}>
                  {subItem?.name}
                </NavLink>
              ) : (
                <Typography sx={subMenuLinkStyle}>{subItem?.name}</Typography>
              )}
            </SubMenuItemWrapper>
          );
        })}
      </SubMenuWrapper>
    </Dropdown>
  );
};

export default MenuItem;
